// Elastic Audio auf dem Projekt (innerhalb von commit()): Modus je Spur, Warp-Marker, Quantisieren ohne Schneiden, Tonhöhe.
import type { Clip, ElasticMode, Project } from '@srs/shared';
import { beatAt } from '@srs/shared';
import { clipMap, ensureWarp, isElastic } from './warp.ts';

/** Elastic Audio einer Spur ein- oder ausschalten (aus: Warp und Tonhöhe verwerfen, Clips spielen wieder 1:1) */
export function setElastic(p: Project, trackId: string, mode: ElasticMode | null) {
  const t = p.tracks.find((x) => x.id === trackId);
  if (!t || t.kind !== 'audio') return;
  if (mode) { t.elastic = mode; for (const c of p.clips) if (c.trackId === trackId && c.kind === 'audio') ensureWarp(p, c); }
  else { t.elastic = undefined; for (const c of p.clips) if (c.trackId === trackId) delete c.warp; }
}
const elasticClip = (p: Project, c: Clip) => c.kind === 'audio' && isElastic(p.tracks.find((t) => t.id === c.trackId));

/** Warp-Marker an der Zeit t setzen (ändert nichts am Klang, hält diese Stelle fest) */
export function addWarp(p: Project, c: Clip, t: number): boolean {
  if (!elasticClip(p, c) || t <= c.start || t >= c.start + c.duration) return false;
  ensureWarp(p, c);
  const s = clipMap(p, c).toSrc(t), b = beatAt(p, t) - beatAt(p, c.start);
  if (c.warp!.points.some((w) => Math.abs(w.s - s) < 0.002)) return false;
  c.warp!.points.push({ s, b }); c.warp!.points.sort((x, y) => x.b - y.b);
  return true;
}
/** Warp-Marker (Index) auf die Zeit t schieben; bleibt zwischen seinen Nachbarn */
export function moveWarp(p: Project, c: Clip, i: number, t: number) {
  const pts = c.warp?.points;
  if (!pts?.[i]) return;
  const b0 = beatAt(p, c.start), lo = (pts[i - 1]?.b ?? -Infinity) + 0.02, hi = (pts[i + 1]?.b ?? Infinity) - 0.02;
  pts[i] = { ...pts[i], b: Math.max(lo, Math.min(hi, beatAt(p, t) - b0)) };
}
export function removeWarp(c: Clip, i: number) {
  const pts = c.warp?.points;
  if (!pts || pts.length <= 2) return;
  pts.splice(i, 1);
}
/** Elastic Pitch: Tonhöhe des Clips in Halbtönen (mit Cent als Nachkommastellen) */
export function setPitch(p: Project, c: Clip, semitones: number) {
  if (!elasticClip(p, c)) return;
  ensureWarp(p, c); c.warp!.pitch = Math.round(semitones * 100) / 100;
}

/**
 * Elastic Quantize: Anschläge aufs Raster ziehen, ohne zu schneiden (die Abschnitte dazwischen werden gedehnt).
 * events: Anschläge in Timeline-Sekunden (über alle gewählten Spuren, damit Mehrspur-Drums phasengleich bleiben),
 * target: Zielzeit je Anschlag. Wirkt auf alle Elastic-Clips der Spuren im Bereich.
 */
export function quantizeElastic(p: Project, r: { start: number; end: number; trackIds: string[] }, events: { t: number; target: number }[]): number {
  let n = 0;
  for (const c of p.clips) {
    if (!r.trackIds.includes(c.trackId) || !elasticClip(p, c)) continue;
    const a = Math.max(c.start, r.start), e = Math.min(c.start + c.duration, r.end);
    if (e - a < 0.01) continue;
    ensureWarp(p, c);
    const m = clipMap(p, c), b0 = beatAt(p, c.start), rel = (t: number) => beatAt(p, t) - b0;
    // Ränder des Bereichs festhalten, bestehende Marker im Bereich ersetzen
    const keep = c.warp!.points.filter((w) => { const t = m.toTime(w.s); return t < a - 1e-4 || t > e + 1e-4; });
    const fixed = [{ s: m.toSrc(a), b: rel(a) }, { s: m.toSrc(e), b: rel(e) }];
    const moved = events.filter((x) => x.t > a + 0.005 && x.t < e - 0.005).map((x) => ({ s: m.toSrc(x.t), b: rel(Math.max(a + 0.005, Math.min(e - 0.005, x.target))) }));
    const pts = [...keep, ...fixed, ...moved].sort((x, y) => x.b - y.b);
    // monoton halten (Ziel darf einen Nachbarn nicht überholen)
    const clean: typeof pts = [];
    for (const w of pts) { const last = clean[clean.length - 1]; if (!last || (w.b > last.b + 0.01 && w.s > last.s + 0.001)) clean.push(w); }
    c.warp!.points = clean;
    n += moved.length;
  }
  return n;
}
